import React, { ChangeEvent, useContext, useState } from "react";
import UiContext from "@contexts/UiContext";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTextArea from "@components/shared/OwTextArea/OwTextArea";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import IVendorModel from "@models/IVendorModel";
import IContact from "@models/IContact";
import IAccount_Note from "@models/IAccount_Note";
import ICreateAccountNoteDTO from "@models/DTO/ICreateAccountNoteDTO";
import IUpdateAccountNoteDTO from "@models/DTO/IUpdateAccountNoteDTO";
import { buildAuthHeadersForFetch, buildRestUrl } from "@services/RestService";
import RestEndpointsEnum from "@models/RestEndpointsEnum";

type Props = {
    vendorModel: IVendorModel;
    vendorContacts: IContact[];
    closeModalFn: () => void;
    onSaveSuccess: () => void;
    existingNote?: IAccount_Note;
};

export const NoteEditorModal = (props: Props) => {
    const uiContext = useContext(UiContext);
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [note, setNote] = useState(props.existingNote?.note || "");
    const [selectedContactId, setSelectedContactId] = useState<number | undefined>(props.existingNote?.contact_id);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e: ChangeEvent<any>) => {
        const { name, value } = e.target;

        if (name === "note") {
            setNote(value);
        } else if (name === "contact") {
            const contactId = value ? parseInt(value) : undefined;
            setSelectedContactId(contactId);
        }
    };

    const handleSave = async () => {
        if (!note.trim()) {
            uiContext.notifyFn(
                {
                    message: "Note text is required",
                    level: "error",
                } as any,
                null,
                true
            );
            return;
        }

        setIsSubmitting(true);

        const selectedContact = props.vendorContacts.find((c) => c.id === selectedContactId);

        let payload: ICreateAccountNoteDTO | IUpdateAccountNoteDTO;

        if (props.existingNote) {
            // Update existing note - only send changed fields
            const updatePayload: IUpdateAccountNoteDTO = {
                details: {
                    contact_id: selectedContactId,
                    contact_name: selectedContact?.name,
                    contact_phone: selectedContact?.phone,
                    contact_email: selectedContact?.email,
                    note: note.trim(),
                },
            };
            payload = updatePayload;
        } else {
            // Create new note - send full object
            const createPayload: ICreateAccountNoteDTO = {
                details: {
                    supplier_id: supplierModel.id,
                    vendor_id: props.vendorModel.id,
                    user_id: userModel.id,
                    contact_id: selectedContactId,
                    contact_name: selectedContact?.name,
                    contact_phone: selectedContact?.phone,
                    contact_email: selectedContact?.email,
                    note: note.trim(),
                },
            };
            payload = createPayload;
        }

        try {
            const endpoint = props.existingNote ? RestEndpointsEnum.AccountNotesUpdate : RestEndpointsEnum.AccountNotesCreate;

            const url = buildRestUrl({
                endpoint,
                params: {
                    id: String(props.existingNote?.id),
                },
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
                vendorModel: props.vendorModel,
            });

            const response = await fetch(url, {
                method: props.existingNote ? "PUT" : "POST",
                headers,
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                uiContext.notifyFn(null, {
                    message: props.existingNote ? "note_updated_successfully" : "note_created_successfully",
                    level: "success",
                    position: "bc",
                });
                props.onSaveSuccess();
                props.closeModalFn();
            } else {
                throw new Error(`Failed to save note: ${response.statusText}`);
            }
        } catch (error) {
            console.error("Error saving note:", error);
            uiContext.notifyFn(
                {
                    message: "error_saving_note",
                    level: "error",
                } as any,
                null,
                true
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={"NoteEditorModal p-3"}>
            <div className={"d-flex justify-content-between align-items-center mb-3"}>
                <OwTitle title={props.existingNote ? "edit_note" : "add_note"} tag={"h3"} />
                <OwButton centerIconClass={"fas fa-times"} fn={props.closeModalFn} className={"btn-danger"} />
            </div>

            <div className={"mb-3"}>
                <OwLabel title={"contact"} />
                <OwSelectOption
                    fn={handleChange}
                    name={"contact"}
                    titleProp={"name"}
                    valueProp={"id"}
                    options={props.vendorContacts}
                    selectedOption={selectedContactId?.toString() || ""}
                    showBlankFirst={true}
                    hasSelectedOptionProp={false}
                />
            </div>

            <div className={"mb-3"}>
                <OwLabel title={"note"} showIsReqAsterik={true} />
                <OwTextArea name={"note"} fn={handleChange} value={note} rows={6} />
            </div>

            <div className={"d-flex justify-content-end"}>
                <OwButton title={"cancel"} fn={props.closeModalFn} className={"btn-secondary me-2"} />
                <OwButton
                    title={"save"}
                    fn={handleSave}
                    leftIconClass={"fas fa-save"}
                    active={isSubmitting}
                    disableOnActive={true}
                    className={"btn-primary"}
                />
            </div>
        </div>
    );
};
